import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { notify } from "../lib/notifications";

export interface ProjectUpdate {
  id: string;
  project_id: string;
  contractor_id: string;
  title: string;
  description: string | null;
  type: "daily" | "weekly" | "milestone";
  created_at: string;
}

export interface UpdateFormData {
  title: string;
  description: string;
  type: "daily" | "weekly" | "milestone";
}

export const useUpdates = (projectId: string) => {
  const [updates, setUpdates] = useState<ProjectUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUpdates = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from("updates")
      .select("id, project_id, contractor_id, title, description, type, created_at")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setUpdates((data ?? []) as ProjectUpdate[]);
    }
    setLoading(false);
  }, [projectId]);

  const createUpdate = useCallback(
    async (contractorId: string, formData: UpdateFormData) => {
      const { data, error: insertError } = await supabase
        .from("updates")
        .insert([
          {
            project_id: projectId,
            contractor_id: contractorId,
            title: formData.title.trim(),
            description: formData.description.trim() || null,
            type: formData.type,
          },
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      setUpdates((prev) => [data as ProjectUpdate, ...prev]);

      // Notify the client about the new update
      const { data: projectData } = await supabase
        .from("projects")
        .select("client_id, title")
        .eq("id", projectId)
        .single();

      if (projectData?.client_id) {
        notify({
          recipientId: projectData.client_id,
          projectId,
          type: "update_posted",
          title: "New project update",
          message: `Your contractor posted a new update: "${formData.title}"`,
          link: `/client/projects/${projectId}`,
        });
      }

      return data as ProjectUpdate;
    },
    [projectId],
  );

  const deleteUpdate = useCallback(async (updateId: string) => {
    const { error: deleteError } = await supabase
      .from("updates")
      .delete()
      .eq("id", updateId);

    if (deleteError) throw deleteError;

    setUpdates((prev) => prev.filter((u) => u.id !== updateId));
  }, []);

  useEffect(() => {
    if (!projectId) return;

    fetchUpdates();

    const channel = supabase
      .channel(`updates:${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "updates",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          const incoming = payload.new as ProjectUpdate;
          setUpdates((prev) => {
            // Skip if we already added it optimistically from createUpdate
            if (prev.some((u) => u.id === incoming.id)) return prev;
            return [incoming, ...prev];
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "updates",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          setUpdates((prev) => prev.filter((u) => u.id !== payload.old.id));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, fetchUpdates]);

  return { updates, loading, error, createUpdate, deleteUpdate, refetch: fetchUpdates };
};
